import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'throttle-debounce';

import SearchBase from '@/components/mui/Layouts/components/AppbarSearchBase';
import SearchCharactersWarning from '@/components/mui/Layouts/components/SearchCharactersWarning';
import SearchTypeButton from '@/components/mui/Layouts/Companies/components/Search/components/SearchTypeButton';
import InputAdornment from '@mui/material/InputAdornment';

import { SEARCH_TYPES, searchTextSelector, searchTypeSelector, setSearchFilterAction } from '@/domain/companies';
import { SEARCH_MIN_CHARACTERS } from '@/domain/router/helpers';

const AppBarSearch = () => {
  const dispatch = useDispatch();
  const searchText = useSelector(searchTextSelector);
  const searchType = useSelector(searchTypeSelector);
  const searchFieldRef = useRef(null);

  const search = useCallback(
    (value) => {
      dispatch(setSearchFilterAction(value));
    },
    [dispatch],
  );

  const debounceSearch = useMemo(() => debounce(500, search), [search]);

  const onChange = useCallback(
    (value) => {
      const action = searchType !== SEARCH_TYPES.byCompany ? debounceSearch : search;
      action(value);
    },
    [searchType, debounceSearch, search],
  );

  useEffect(() => {
    if (searchFieldRef && searchFieldRef.current) {
      const searchInput = searchFieldRef.current.querySelector('input');
      if (searchInput) {
        searchInput.focus();
      }
    }
  }, [searchType]);

  const displayCharacterThreasholdWarning =
    searchText.length < SEARCH_MIN_CHARACTERS && searchText.length > 0 && searchType !== SEARCH_TYPES.byCompany;

  const endAdornment = (
    <InputAdornment position="end">
      {displayCharacterThreasholdWarning && <SearchCharactersWarning />}
      <SearchTypeButton />
    </InputAdornment>
  );

  return (
    <SearchBase
      ref={searchFieldRef}
      value={searchText}
      onChange={onChange}
      slotProps={{
        input: { endAdornment },
      }}
    />
  );
};

export default AppBarSearch;
